import { createContext, type ComponentChildren } from 'preact';
import { useContext, useState, useCallback, useEffect } from 'preact/hooks';
import type { Currency, TimeWindow, Theme } from '../../shared/types';
import { getInitialTheme, persistTheme, watchSystem } from './theme';

const KEY_CCY = 'mnw.ccy';
const KEY_WIN = 'mnw.win';
const KEY_PRIVACY = 'mnw.privacy';

const CurrencyCtx = createContext<{ ccy: Currency; setCcy: (c: Currency) => void } | null>(null);
const WindowCtx = createContext<{ win: TimeWindow; setWin: (w: TimeWindow) => void } | null>(null);
const ThemeCtx = createContext<{ theme: Theme; setTheme: (t: Theme) => void } | null>(null);
const PrivacyCtx = createContext<{ hidden: boolean; toggle: () => void } | null>(null);

export function AppProviders({ children }: { children: ComponentChildren }) {
  const [ccy, setCcyState] = useState<Currency>(
    () => (localStorage.getItem(KEY_CCY) as Currency) || 'USD'
  );
  const [win, setWinState] = useState<TimeWindow>(
    () => (localStorage.getItem(KEY_WIN) as TimeWindow) || '30D'
  );
  const [theme, setThemeState] = useState<Theme>(() => getInitialTheme());
  const [hidden, setHidden] = useState<boolean>(
    () => localStorage.getItem(KEY_PRIVACY) === '1'
  );

  useEffect(() => {
    persistTheme(theme);
    if (theme !== 'system') return;
    return watchSystem(() => persistTheme('system'));
  }, [theme]);

  const togglePrivacy = useCallback(() => {
    setHidden((h) => {
      const next = !h;
      localStorage.setItem(KEY_PRIVACY, next ? '1' : '0');
      return next;
    });
  }, []);

  const setCcy = useCallback((c: Currency) => {
    setCcyState(c);
    localStorage.setItem(KEY_CCY, c);
  }, []);
  const setWin = useCallback((w: TimeWindow) => {
    setWinState(w);
    localStorage.setItem(KEY_WIN, w);
  }, []);

  return (
    <CurrencyCtx.Provider value={{ ccy, setCcy }}>
      <WindowCtx.Provider value={{ win, setWin }}>
        <ThemeCtx.Provider value={{ theme, setTheme: setThemeState }}>
          <PrivacyCtx.Provider value={{ hidden, toggle: togglePrivacy }}>
            {children}
          </PrivacyCtx.Provider>
        </ThemeCtx.Provider>
      </WindowCtx.Provider>
    </CurrencyCtx.Provider>
  );
}

export function useCurrency() {
  const c = useContext(CurrencyCtx);
  if (!c) throw new Error('useCurrency outside provider');
  return c;
}
export function useTimeWindow() {
  const w = useContext(WindowCtx);
  if (!w) throw new Error('useTimeWindow outside provider');
  return w;
}
export function useTheme() {
  const t = useContext(ThemeCtx);
  if (!t) throw new Error('useTheme outside provider');
  return t;
}
export function usePrivacy() {
  const p = useContext(PrivacyCtx);
  if (!p) throw new Error('usePrivacy outside provider');
  return p;
}
